<template>
  <Teleport to="body">
    <transition name="fade">
      <div v-if="show" class="fixed inset-0 flex items-center justify-center p-4 sm:p-6" style="z-index: 999999;">
        <div class="absolute inset-0 bg-black/50"></div>
      
      <div class="relative bg-white w-full max-w-[900px] h-[640px] max-h-[90vh] rounded-2xl shadow-[0_40px_100px_-20px_rgba(0,0,0,0.6)] flex flex-col overflow-hidden animate-bounce-in border border-gray-100 m-auto">
        
        <!-- Header -->
        <div class="bg-white px-6 py-3 flex items-center justify-between border-b border-gray-200 shrink-0">
          <div class="flex items-center gap-4">
            <span class="text-gray-400 text-sm font-medium">Editor mode</span>
          </div>
          <div class="flex items-center gap-3">
             <button @click="handleDone" :disabled="processingDone" class="text-white bg-blue-400 hover:bg-blue-500 transition-colors shadow-md px-10 py-2 rounded font-bold text-sm transition active:scale-95 disabled:opacity-50 flex items-center gap-2">
                <div v-if="processingDone" class="w-4 h-4 border-2 border-white/30 border-t-white rounded animate-spin"></div>
                {{ processingDone ? 'Processing...' : 'Done' }}
              </button>
              <button @click="$emit('close')" :disabled="processingDone" class="p-1.5 hover:bg-gray-100 rounded-md transition text-gray-400 hover:text-gray-900">
                <X :size="20" />
              </button>
          </div>
        </div>

        <div class="flex-1 flex overflow-hidden">
          <!-- Sidebar Icons -->
          <div class="w-16 bg-white border-r border-gray-100 flex flex-col items-center py-6 gap-8 shrink-0">
             <button @click="activeTab = 'size'" :class="['p-3 rounded-xl transition', activeTab === 'size' ? 'bg-gray-100 text-slate-900' : 'text-gray-400 hover:text-gray-600']">
                <Maximize :size="24" />
             </button>
             <button @click="activeTab = 'bg'" :class="['p-3 rounded-xl transition', activeTab === 'bg' ? 'bg-gray-100 text-slate-900' : 'text-gray-400 hover:text-gray-600']">
                <Palette :size="24" />
             </button>
          </div>

          <!-- Options Panel -->
          <div class="w-72 bg-white border-r border-gray-100 flex flex-col shrink-0 scrollbar-hide transition-all">
            <!-- Size Tab Content -->
            <div v-if="activeTab === 'size'" class="p-4 flex flex-col h-full gap-5 overflow-hidden animate-fade-in">
              <!-- Paper Size -->
              <div class="shrink-0">
                <label class="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-2 block">Paper Size</label>
                <div class="relative">
                  <select class="w-full bg-white border border-gray-200 rounded-lg py-2.5 px-3 text-sm focus:outline-none appearance-none cursor-not-allowed" disabled>
                    <option>Individual/single photo</option>
                  </select>
                  <ChevronDown class="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" :size="16" />
                </div>
              </div>
              <!-- Custom Size -->
              <div class="shrink-0">
                <label class="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-2 block">Custom Size</label>
                <div class="flex items-center gap-3">
                  <input type="number" v-model="customWidth" readonly class="w-full border border-gray-200 rounded-lg py-2 px-3 text-sm outline-none bg-gray-50/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="W" />
                  <span class="text-gray-400">x</span>
                  <input type="number" v-model="customHeight" readonly class="w-full border border-gray-200 rounded-lg py-2 px-3 text-sm outline-none bg-gray-50/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" placeholder="H" />
                </div>
              </div>

              <!-- Zoom Slider -->
              <div class="shrink-0">
                <label class="text-[10px] uppercase tracking-wider font-bold text-gray-400 mb-2 block flex justify-between">
                  <span>Zoom</span>
                  <span class="text-blue-500">{{ Math.round(zoomLevel * 100) }}%</span>
                </label>
                <input type="range" v-model.number="zoomLevel" min="1" max="3" step="0.05" class="w-full form-range accent-blue-500 hover:accent-blue-600 transition" />
              </div>

              <!-- Scrollable Presets Section -->
              <div class="flex-1 flex flex-col min-h-0 border-t border-gray-50 pt-4">
                <!-- Search -->
                <div class="relative mb-3 shrink-0">
                  <Search class="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" :size="16" />
                  <input type="text" v-model="searchQuery" placeholder="Search size" class="w-full bg-gray-50 border-none rounded-xl py-2.5 pl-10 pr-4 text-sm focus:ring-2 focus:ring-blue-500/20" />
                </div>

                <!-- Size Presets List -->
                <div class="flex-1 overflow-y-auto space-y-2 pr-1 scrollbar-hide">
                  <div v-for="size in filteredPresetSizes" :key="size.id" 
                       @click="selectedSize = size"
                       :class="['p-3 rounded-xl border transition cursor-pointer group', 
                                selectedSize?.id === size.id ? 'border-blue-500 bg-blue-50/50 shadow-sm' : 'border-gray-100 hover:bg-gray-50']">           
                    <div class="flex items-center justify-between">
                      <div class="flex flex-col">
                        <span :class="['text-xs font-bold', selectedSize?.id === size.id ? 'text-blue-600' : 'text-gray-800']">{{ size.name }}</span>
                        <span class="text-[10px] text-gray-500">{{ size.desc }}</span>
                      </div>
                      <div v-if="selectedSize?.id === size.id" class="w-4 h-4 rounded-full bg-blue-500 flex items-center justify-center shrink-0">
                        <svg class="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Background Tab Content -->
            <div v-if="activeTab === 'bg'" class="p-4 space-y-5 overflow-y-auto h-full animate-fade-in scrollbar-hide">
                <div>
                   <h3 class="text-sm font-medium text-gray-500 mb-3 block">Classical background color</h3>
                   <div class="grid grid-cols-5 gap-3">
                       <button 
                           v-for="(color, index) in classicalColors" 
                           :key="'c'+index"
                           @click="selectColor(color.css, color.val)"
                           class="relative w-11 h-11 rounded-[10px] shadow-sm border transition-all overflow-hidden"
                           :class="selectedBgColor === color.css ? 'ring-1 ring-black ring-offset-1 border-transparent scale-105' : 'border-gray-200'"
                           :style="{ background: color.css === 'transparent' ? 'white' : color.css }"
                       >
                           <div v-if="color.css === 'transparent'" class="absolute inset-0 result-checkerboard opacity-50"></div>
                       </button>
                   </div>
                </div>

                <div>
                   <h3 class="text-sm font-medium text-gray-500 mb-3 block mt-2">Stylish background color</h3>
                   <div class="grid grid-cols-5 gap-3">
                       <!-- Custom Color Picker Icon (Rainbow) -->
                       <div class="relative w-11 h-11">
                          <input type="color" v-model="customColor" @input="updateCustomColor" class="absolute inset-0 opacity-0 cursor-pointer w-full h-full z-10" />
                          <div class="w-full h-full rounded-[10px] shadow-sm border border-gray-200 transition-transform hover:scale-105"
                               style="background: conic-gradient(red, yellow, lime, aqua, blue, magenta, red);">
                          </div>
                          <!-- Highlight Ring for Custom Color if active -->
                          <div v-if="isCustomColorSelected" class="absolute -inset-1 rounded-[14px] ring-2 ring-blue-500 ring-offset-1 pointer-events-none"></div>
                       </div>
                       
                       <button 
                           v-for="(color, index) in stylishColors" 
                           :key="'s'+index"
                           @click="selectColor(color.css, color.val)"
                           class="w-11 h-11 rounded-[10px] shadow-sm border transition-all"
                           :class="selectedBgColor === color.css ? 'ring-1 ring-black ring-offset-1 border-transparent scale-105' : 'border-gray-200'"
                           :style="{ background: color.css }"
                       >
                       </button>
                   </div>
                </div>
            </div>


          </div>

          <!-- Main View -->
          <div class="flex-1 flex items-center justify-center p-6 bg-[#f0f2f5] overflow-hidden">
             <!-- The white frame container -->
             <div class="bg-white p-2 shadow-sm flex items-center justify-center transition-all duration-300"
                  :style="{
                    width: 'auto',
                    height: 'auto',
                    maxHeight: '100%',
                    maxWidth: '100%',
                    aspectRatio: (selectedSize ? selectedSize.w / selectedSize.h : customWidth / customHeight)
                  }">
               <!-- The actual photo container -->
               <div ref="previewBox" class="relative w-full h-full overflow-hidden"
                    :style="{
                      background: selectedBgColor === 'transparent' ? 'transparent' : selectedBgColor
                    }">
                 <div v-if="selectedBgColor === 'transparent'" class="absolute inset-0 result-checkerboard opacity-50"></div>
                 
                 <!-- Loading Overlay for Auto-Framing -->
                 <div v-if="autoFraming" class="absolute inset-0 z-20 bg-white/60 flex flex-col items-center justify-center gap-3">
                    <div class="w-8 h-8 border-3 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    <span class="text-[10px] font-bold text-blue-600 uppercase tracking-widest">AI Framing...</span>
                 </div>

                 <img 
                   :src="editorPreview || img" 
                   alt="Subject" 
                   class="relative z-10 cursor-move select-none" 
                   :style="{ 
                     width: '100%',
                     height: '100%',
                     objectFit: 'cover',
                     objectPosition: 'center top', 
                     transform: `translate(${dragX}px, ${dragY}px) scale(${zoomLevel})`, 
                     transformOrigin: 'center top', 
                     transition: isDragging ? 'none' : 'transform 0.1s' 
                   }" 
                   @mousedown="startDrag"
                 />
               </div>
             </div>
          </div>
        </div>
      </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
import { X, Maximize, Palette, Search, ChevronDown, Check } from 'lucide-vue-next';
import axios from 'axios';

export default {
  name: 'PassportPhotoEditor',
  components: { X, Maximize, Palette, Search, ChevronDown, Check },
  props: {
    show: Boolean,
    img: String,
    initialSize: Object,
    initialColor: String
  },
  data() {
    return {
      activeTab: 'size',
      customWidth: 413,
      customHeight: 531,
      zoomLevel: 1.0,
      dragX: 0,
      dragY: 0,
      isDragging: false,
      startX: 0,
      startY: 0,
      selectedSize: null,
      selectedBgColor: 'transparent',
      selectedBgColorCanvas: 'transparent',
      customColor: '#ff0000',
      processingDone: false,
      autoFraming: false,
      editorPreview: null,
      apiBaseUrl: 'http://127.0.0.1:5000',
      searchQuery: '',
      isFreshImage: true,
      classicalColors: [
        { css: '#4185d6', val: '#4185d6' },
        { css: '#73B8E8', val: '#73B8E8' },
        { css: '#2292FE', val: '#2292FE' },
        { css: '#2c3e50', val: '#2c3e50' },
        { css: 'transparent', val: 'transparent' }

      ],
      stylishColors: [
        { css: 'white', val: 'white' },
        { css: 'linear-gradient(to bottom, #8abefb, #cee9f6)', val: '#cee9f6' }, 
        { css: '#aed1d9', val: '#aed1d9' },
        { css: 'linear-gradient(to bottom, #999999, #f7f7f7)', val: '#cfcfcf' }, 
        { css: '#c7e0c7', val: '#c7e0c7' },
        { css: '#40805c', val: '#40805c' },
        { css: '#6fb88f', val: '#6fb88f' },
        { css: '#c7e6d7', val: '#c7e6d7' },
        { css: '#8f95a1', val: '#8f95a1' },
        { css: '#416bbc', val: '#416bbc' },
        { css: '#00b4d8', val: '#00b4d8' },
        { css: '#e2d0ab', val: '#e2d0ab' },
        { css: '#ce6841', val: '#ce6841' },
        { css: '#ddb982', val: '#ddb982' },
        { css: '#000000', val: '#000000' },
        { css: '#fadce2', val: '#fadce2' },
        { css: '#f4b9ae', val: '#f4b9ae' },
        { css: '#c92a35', val: '#c92a35' },
        { css: '#ff0909', val: '#ff0909' },

      ],
      presetSizes: [
        { id: 1, name: 'Standard Passport', desc: '(3.5 x 4.5 cm)(413*531px)', w: 413, h: 531 },
        { id: 2, name: 'US Passport / Visa', desc: '(2 x 2")(600*600px)', w: 600, h: 600 },
        { id: 3, name: 'Small Photo', desc: '(3x4cm)(354*472px)', w: 354, h: 472 },
        { id: 4, name: 'Albania(Passport)', desc: '(4x5cm)(472*590px)', w: 472, h: 590 },
        { id: 5, name: 'Australia(Passport)', desc: '(3.5 x 4.5 cm)(413*531px)', w: 413, h: 531 },
        { id: 6, name: 'Austria(Passport)', desc: '(3.5 x 4.5 cm)(413*531px)', w: 413, h: 531 },
        { id: 7, name: 'Bangladesh(Passport)', desc: '(4x5cm)(472*590px)', w: 472, h: 590 },
        { id: 8, name: 'Belarus(Passport)', desc: '(4x5cm)(472*590px)', w: 472, h: 590 },
        { id: 9, name: 'Belgium(Passport)', desc: '(3.5 x 4.5 cm)(413*531px)', w: 413, h: 531 },
        { id: 10, name: 'Belize(Passport)', desc: '(2 x 2")(600*600px)', w: 600, h: 600 },
        { id: 11, name: 'Bolivia(Passport)', desc: '(2 x 2")(600*600px)', w: 600, h: 600 },
        { id: 12, name: 'Brazil(Passport)', desc: '(5x7cm)(590*826px)', w: 590, h: 826 },
        { id: 13, name: 'Brazil(Visa)', desc: '(2 x 2")(600*600px)', w: 600, h: 600 },
        { id: 14, name: 'Canada(Passport)', desc: '(5x7cm)(590*826px)', w: 590, h: 826 },
        { id: 15, name: 'China(Passport)', desc: '(33x48mm)(390*567px)', w: 390, h: 567 },
        { id: 16, name: 'France(Passport)', desc: '(3.5x4.5cm)(413*531px)', w: 413, h: 531 },
        { id: 17, name: 'Germany(Passport)', desc: '(3.5x4.5cm)(413*531px)', w: 413, h: 531 },
        { id: 18, name: 'India(Passport)', desc: '(3.5x4.5cm)(413*531px)', w: 413, h: 531 },
        { id: 19, name: 'Italy(Passport)', desc: '(3.5x4.5cm)(413*531px)', w: 413, h: 531 },
        { id: 20, name: 'Japan(Passport)', desc: '(3.5x4.5cm)(413*531px)', w: 413, h: 531 },
        { id: 21, name: 'Netherlands(Passport)', desc: '(3.5x4.5cm)(413*531px)', w: 413, h: 531 },
        { id: 22, name: 'Pakistan(Passport)', desc: '(3.5x4.5cm)(413*531px)', w: 413, h: 531 },
        { id: 23, name: 'Saudi Arabia(Passport)', desc: '(4x6cm)(472*709px)', w: 472, h: 709 },
        { id: 24, name: 'UAE(Passport)', desc: '(4x6cm)(472*709px)', w: 472, h: 709 },
        { id: 25, name: 'UK(Passport)', desc: '(3.5x4.5cm)(413*531px)', w: 413, h: 531 },
      ]
    };
  },
  computed: {
    isCustomColorSelected() {
      return !this.classicalColors.find(c => c.css === this.selectedBgColor) && 
             !this.stylishColors.find(c => c.css === this.selectedBgColor) &&
             this.selectedBgColor !== 'transparent' &&
             this.selectedBgColor !== 'white';
    },
    filteredPresetSizes() {
      if (!this.searchQuery) return this.presetSizes;
      const query = this.searchQuery.toLowerCase();
      return this.presetSizes.filter(size => 
        size.name.toLowerCase().includes(query) || 
        size.desc.toLowerCase().includes(query)
      );
    }
  },
  watch: {
    selectedBgColor(newVal) {
      this.$emit('change', { color: newVal, size: this.selectedSize });
    },
    selectedSize: {
      handler(newVal) {
        this.$emit('change', { color: this.selectedBgColor, size: newVal });
      },
      deep: true
    },
    img(newVal, oldVal) {
      // Sirf tab poora reset karo jab nai image aaye
      if (newVal && newVal !== oldVal) {
        this.selectedBgColor = 'transparent';
        this.selectedBgColorCanvas = 'transparent';
        this.customColor = '#ff0000';
        this.isFreshImage = true;
      }
    },
    show(newVal) {
       if (newVal && this.img) {
          if (this.isFreshImage) {
             this.resetEditor();
             this.runAutoFraming();
             this.isFreshImage = false;
          }
       } else if (!newVal) {
          this.editorPreview = null;
       }
    }
  },
  methods: {
    resetEditor() {
      this.activeTab = 'size';
      this.customWidth = 413;
      this.customHeight = 531;
      this.zoomLevel = 1.0;
      this.dragX = 0;
      this.dragY = 0;
      
      if (this.initialSize) {
        const found = this.presetSizes.find(s => s.id === this.initialSize.id || (s.w === this.initialSize.w && s.h === this.initialSize.h));
        this.selectedSize = found || this.initialSize;
      } else {
        this.selectedSize = this.presetSizes[0];
      }
      
      if (this.initialColor) {
        this.selectedBgColor = this.initialColor;
        this.selectedBgColorCanvas = this.initialColor;
      } else {
        this.selectedBgColor = 'transparent';
        this.selectedBgColorCanvas = 'transparent';
      }
      
      this.processingDone = false;
      this.autoFraming = false;
    },
    async runAutoFraming() {
       this.autoFraming = true;
       try {
          // Convert the current transparent URL back to a blob to send to face detector
          const response = await fetch(this.img);
          const blob = await response.blob();
          
          const formData = new FormData();
          formData.append('image', blob);

          const res = await axios.post(`${this.apiBaseUrl}/detect-face`, formData);
          const data = res.data;

          if (data.face_found) {
             // 1. Calculate ideal zoom level
             // We want the face height to be about 50-60% of the frame height
             // Current box in editor is ~280px wide. 
             // Let's calculate based on the original image dimensions.
             const box_w = this.selectedSize ? this.selectedSize.w : this.customWidth;
             const box_h = this.selectedSize ? this.selectedSize.h : this.customHeight;
             
             // Base scale matches "object-cover" (filling the box)
             const baseScale = Math.max(box_w / data.img_w, box_h / data.img_h);
             
             // Target: face height 'h' should be about 40-45% of box height (less aggressive)
             this.zoomLevel = (box_h * 0.42) / (data.h * baseScale);
             
             // Clamp zoom
             this.zoomLevel = Math.min(Math.max(this.zoomLevel, 1), 3);

             // How much to move the image in "preview pixels"
             const previewBoxWidth = this.$refs.previewBox ? this.$refs.previewBox.clientWidth : 280;
             const previewScaleFactor = previewBoxWidth / box_w;
             
             // Center face horizontally
             const faceX = (data.x + data.w / 2);
             this.dragX = ((data.img_w / 2) - faceX) * baseScale * previewScaleFactor;
             
             // Position face towards the top
             const faceY = data.y;
             const targetFaceYPosInBox = box_h * 0.20; // 20% from top
             this.dragY = (targetFaceYPosInBox - (faceY * baseScale)) * previewScaleFactor;
             
             // Transition smoothness
             setTimeout(() => { this.isDragging = false; }, 100);
          }
       } catch (err) {
          console.error("Smart Framing failed:", err);
       } finally {
          this.autoFraming = false;
       }
    },
    selectColor(css, val) {
       this.selectedBgColor = css;
       this.selectedBgColorCanvas = val;
    },
    updateCustomColor(event) {
       const val = event.target.value;
       this.selectColor(val, val);
    },
    startDrag(e) {
      if (e.button !== 0) return; // Only allow left click
      e.preventDefault(); // Prevent default image dragging
      
      this.isDragging = true;
      this.startX = e.clientX - this.dragX;
      this.startY = e.clientY - this.dragY;
      
      window.addEventListener('mousemove', this.onDrag);
      window.addEventListener('mouseup', this.stopDrag);
    },
    onDrag(e) {
      if (!this.isDragging) return;
      this.dragX = e.clientX - this.startX;
      this.dragY = e.clientY - this.startY;
    },
    stopDrag() {
      this.isDragging = false;
      window.removeEventListener('mousemove', this.onDrag);
      window.removeEventListener('mouseup', this.stopDrag);
    },
    handleDone() {
      this.processingDone = true;
      
      const img = new Image();
      img.crossOrigin = "anonymous";
      
      img.onload = () => {
        const canvas = document.createElement('canvas');
        
        // Target dimensions from selected size or custom input
        const targetW = this.selectedSize ? this.selectedSize.w : parseInt(this.customWidth);
        const targetH = this.selectedSize ? this.selectedSize.h : parseInt(this.customHeight);
        
        canvas.width = targetW;
        canvas.height = targetH;
        
        const ctx = canvas.getContext('2d');
        
        // Quality Settings
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        
        // 1. Fill Background
        if (this.selectedBgColor === 'transparent') {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
        } else {
            ctx.fillStyle = this.selectedBgColorCanvas || this.selectedBgColor;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        
        // 2. Calculate Base "Cover" Scaling
        const baseScale = Math.max(targetW / img.width, targetH / img.height);
        
        // Calculate the effective image size after base scale
        const scaledW = img.width * baseScale;
        const scaledH = img.height * baseScale;
        
        // Map the preview drag pixels back to the real canvas pixels
        const previewBoxWidth = this.$refs.previewBox ? this.$refs.previewBox.clientWidth : 280;
        const previewToCanvasFactor = targetW / previewBoxWidth;
        
        // X/Y offsets for "center top" alignment
        const offsetX = (targetW - scaledW) / 2;
        const offsetY = 0; // because object-position: top
        
        const finalX = offsetX + (this.dragX * previewToCanvasFactor);
        const finalY = offsetY + (this.dragY * previewToCanvasFactor);
        
        // Draw the image scaled and translated
        ctx.save();
        ctx.translate(finalX + (scaledW/2), finalY); 
        ctx.scale(this.zoomLevel, this.zoomLevel);
        ctx.drawImage(img, -scaledW/2, 0, scaledW, scaledH);
        ctx.restore();
        
        canvas.toBlob((blob) => {
          const editedUrl = URL.createObjectURL(blob);
          this.processingDone = false;
          this.$emit('confirm', editedUrl);
        }, 'image/png', 1.0);
      };

      img.src = this.editorPreview || this.img;
    }
  },
  mounted() {
    if (this.initialSize) {
      const found = this.presetSizes.find(s => s.id === this.initialSize.id || (s.w === this.initialSize.w && s.h === this.initialSize.h));
      this.selectedSize = found || this.initialSize;
    } else {
      this.selectedSize = this.presetSizes[0];
    }
    if (this.initialColor) {
      this.selectedBgColor = this.initialColor;
      this.selectedBgColorCanvas = this.initialColor;
    }
  }
};
</script>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.animate-fade-in {
  animation: fade-in 0.3s ease-out;
}

@keyframes fade-in {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.result-checkerboard {
  background-image: 
    linear-gradient(45deg, #eee 25%, transparent 25%), 
    linear-gradient(-45deg, #eee 25%, transparent 25%), 
    linear-gradient(45deg, transparent 75%, #eee 75%), 
    linear-gradient(-45deg, transparent 75%, #eee 75%);
  background-size: 10px 10px;
  background-position: 0 0, 0 5px, 5px -5px, -5px 0px;
}
</style>
